import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Button, FormField, Select, Modal, ModalContent } from '../common';
import { saveMatchPreferenceMaybeOffline } from '../../utils/session/saveMatchPreferenceMaybeOffline';

/**
 * Match preference:
 * - `cards` — option cards; tap-to-save by default, or draft + Save when `explicitSave` is set.
 * - `inline` — Type + Strictness dropdowns when enabled; parent supplies header switch via ref `toggleInlineMaster()`.
 * - `modal` — full form in a bottom sheet / dialog with Save (e.g. player management).
 * - `icon` — compact popover with Save.
 */
const INLINE_GAME_TYPE_OPTIONS = [
  { value: 'prefer_mixed', label: 'Mixed' },
  { value: 'prefer_levels', label: 'Levels' },
];

const INLINE_STRENGTH_OPTIONS = [
  { value: 'soft', label: 'Soft' },
  { value: 'hard', label: 'Hard' },
];

/** Tooltip / screen-reader text for the strictness info control (soft vs hard). */
const STRICTNESS_INFO =
  'Soft may still place you in other game types, but weights games matching your preference higher. Hard only allows this game type—you may wait longer until a possible game is available.';
/** Tooltip for the Any lineup preference card. */
const ANY_LINEUP_INFO =
  'Fastest to get a game — the matchmaker can use any doubles type.';

function PreferenceInfoButton({ text, ariaLabel, className = '' }) {
  if (!text) return null;
  return (
    <button
      type="button"
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-text-muted transition hover:bg-surface-subtle hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${className}`.trim()}
      title={text}
      aria-label={ariaLabel || text}
      onClick={(e) => e.stopPropagation()}
    >
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    </button>
  );
}

const TYPE_OPTIONS = [
  {
    value: 'no_preference',
    label: 'No preference',
    shortLabel: 'Any lineup',
    icon: '⚡',
    infoText: ANY_LINEUP_INFO,
  },
  {
    value: 'prefer_mixed',
    label: 'Mixed Doubles',
    shortLabel: 'Mixed Doubles',
    icon: '🎭',
  },
  {
    value: 'prefer_levels',
    label: 'Levels Doubles',
    shortLabel: 'Levels Doubles',
    icon: '⚖️',
  },
];

/** Shared classes for the compact on/off switch (viewer header + optional in-component use). */
export const GAME_PREF_SWITCH_TRACK_CLASS =
  'relative h-7 w-[2.75rem] shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
export const GAME_PREF_SWITCH_THUMB_CLASS =
  'pointer-events-none absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform';

const MatchPreferenceSelector = forwardRef(function MatchPreferenceSelector({
  variant = 'icon',
  open: modalOpen = false,
  onClose = null,
  playerId,
  sessionId,
  currentPreference = 'no_preference',
  currentStrength = 'soft',
  playerName,
  disabled = false,
  /** When true, cards variant updates draft only until Save is pressed. */
  explicitSave = false,
  onPreferenceChange,
  offlineQueue = null,
  session = null,
  getSnapshot = null,
}, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftType, setDraftType] = useState('no_preference');
  const [draftStrength, setDraftStrength] = useState('soft');
  const [isSaving, setIsSaving] = useState(false);

  const isModal = variant === 'modal';
  const panelVisible = isModal ? modalOpen : isOpen;

  const syncDraftFromProps = () => {
    setDraftType(currentPreference || 'no_preference');
    setDraftStrength(currentStrength === 'hard' ? 'hard' : 'soft');
  };

  useEffect(() => {
    if (variant === 'inline' || variant === 'cards') {
      if (isSaving) return;
      syncDraftFromProps();
      return;
    }
    if (panelVisible) {
      syncDraftFromProps();
    }
  }, [panelVisible, currentPreference, currentStrength, variant, isSaving]);

  const isDirty = useMemo(() => {
    const s = draftType === 'no_preference' ? 'soft' : draftStrength;
    return draftType !== currentPreference || s !== (currentStrength === 'hard' ? 'hard' : 'soft');
  }, [draftType, draftStrength, currentPreference, currentStrength]);

  useEffect(() => {
    if (!panelVisible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (isModal && onClose) onClose();
        else setIsOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [panelVisible, isModal, onClose]);

  const handleOpen = (e) => {
    e?.stopPropagation?.();
    syncDraftFromProps();
    setIsOpen(true);
  };

  const closeUi = () => {
    if (isModal && onClose) onClose();
    else setIsOpen(false);
  };

  /**
   * @param {'soft'|'hard'} strengthWhenActive — Soft vs hard when type is mixed or levels.
   */
  const savePreferenceValues = async (
    typeArg,
    strengthWhenActive,
    { closeAfter = false } = {},
  ) => {
    if (isSaving || disabled || !sessionId) return;
    const strengthOut = typeArg === 'no_preference' ? 'soft' : strengthWhenActive || 'soft';
    setIsSaving(true);
    try {
      const result = await saveMatchPreferenceMaybeOffline({
        sessionId,
        playerId,
        matchPreference: typeArg,
        preferenceStrength: strengthOut,
        offlineQueue,
        session,
        snapshot: getSnapshot?.() || null,
      });

      if (onPreferenceChange) {
        onPreferenceChange(
          playerId,
          typeArg,
          strengthOut,
          result?.updated_at || new Date().toISOString(),
        );
      }
      if (closeAfter) closeUi();
    } catch (err) {
      console.error('Failed to update match preference:', err);
      syncDraftFromProps();
      alert('Could not update preference. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const latestForImperative = useRef({});
  latestForImperative.current = {
    draftType,
    draftStrength,
    disabled,
    sessionId,
    savePreferenceValues,
    setDraftType,
    setDraftStrength,
  };

  useImperativeHandle(ref, () => ({
    toggleInlineMaster() {
      const l = latestForImperative.current;
      if (l.disabled || !l.sessionId) return;
      if (l.draftType === 'no_preference') {
        l.setDraftType('prefer_mixed');
        const str = l.draftStrength === 'hard' ? 'hard' : 'soft';
        void l.savePreferenceValues('prefer_mixed', str, { closeAfter: false });
      } else {
        l.setDraftType('no_preference');
        l.setDraftStrength('soft');
        void l.savePreferenceValues('no_preference', 'soft', { closeAfter: false });
      }
    },
  }));

  const persist = async () => {
    await savePreferenceValues(draftType, draftStrength, { closeAfter: true });
  };

  const hasActivePreference = currentPreference !== 'no_preference';
  const isHard = hasActivePreference && currentStrength === 'hard';

  const getButtonIcon = () => {
    if (currentPreference === 'prefer_mixed') return '🎭';
    if (currentPreference === 'prefer_levels') return '⚖️';
    return '⚡';
  };

  const getButtonTitle = () => {
    if (!hasActivePreference) return 'No preference';
    const typeLabel = currentPreference === 'prefer_mixed' ? 'Mixed Doubles' : 'Levels Doubles';
    const strengthLabel = currentStrength === 'hard' ? 'Hard' : 'Soft';
    return `${typeLabel} (${strengthLabel})`;
  };

  const showStrength = draftType !== 'no_preference';

  const preferenceEnabled = draftType !== 'no_preference';

  const onGameTypeChange = (e) => {
    const v = e.target.value;
    if (v !== 'prefer_mixed' && v !== 'prefer_levels') return;
    setDraftType(v);
    void savePreferenceValues(v, draftStrength);
  };

  const onStrengthChange = (e) => {
    const v = e.target.value;
    if (v !== 'soft' && v !== 'hard') return;
    setDraftStrength(v);
    void savePreferenceValues(draftType, v);
  };

  if (variant === 'cards') {
    const selectGameType = (value) => {
      setDraftType(value);
      if (value === 'no_preference') setDraftStrength('soft');
      if (!explicitSave) {
        if (value === 'no_preference') {
          void savePreferenceValues('no_preference', 'soft');
        } else {
          void savePreferenceValues(value, draftStrength);
        }
      }
    };

    const selectStrength = (strength) => {
      setDraftStrength(strength);
      if (!explicitSave) {
        void savePreferenceValues(draftType, strength);
      }
    };

    return (
      <div className="space-y-3" aria-busy={isSaving}>
        <fieldset className="space-y-2">
          <legend className="sr-only">Game type preference</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {TYPE_OPTIONS.map((opt) => {
              const selected = draftType === opt.value;
              return (
                <div
                  key={opt.value}
                  className={`relative rounded-xl border transition ${
                    selected
                      ? 'border-primary bg-primary/10 ring-1 ring-primary/35'
                      : 'border-border-medium bg-surface-subtle hover:border-border hover:bg-surface'
                  } ${isSaving || disabled ? 'opacity-60' : ''}`}
                >
                  <button
                    type="button"
                    disabled={isSaving || disabled}
                    onClick={() => selectGameType(opt.value)}
                    className={`flex min-h-[3.25rem] w-full items-start gap-2.5 px-3 py-3 text-left sm:min-h-0 sm:flex-col sm:items-stretch sm:py-2.5 ${
                      isSaving || disabled ? 'cursor-not-allowed' : ''
                    } ${opt.infoText ? 'pr-10' : selected ? 'pr-8' : ''}`}
                  >
                    <span className="text-xl leading-none" aria-hidden>
                      {opt.icon}
                    </span>
                    <span className="min-w-0 flex-1 text-sm font-semibold text-text-primary">{opt.shortLabel}</span>
                    {selected ? (
                      <span className="shrink-0 text-primary sm:self-end" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                  {opt.infoText ? (
                    <PreferenceInfoButton
                      text={opt.infoText}
                      ariaLabel="Explain Any lineup"
                      className="absolute right-2 top-2.5"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </fieldset>

        {showStrength ? (
          <div className="rounded-xl border border-border-medium bg-surface-subtle p-3">
            <div className="mb-2 text-xs font-medium text-text-secondary">How strict?</div>
            <p className="mb-3 text-xs leading-snug text-text-muted">{STRICTNESS_INFO}</p>
            <div
              className="flex rounded-lg border border-border-medium bg-surface p-0.5"
              role="group"
              aria-label="Preference strength"
            >
              {(['soft', 'hard']).map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={isSaving || disabled}
                  onClick={() => selectStrength(s)}
                  className={`flex-1 rounded-md py-2.5 text-sm font-medium transition ${
                    draftStrength === s
                      ? 'bg-primary text-text-inverse shadow-sm'
                      : 'text-text-secondary hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {s === 'soft' ? 'Soft' : 'Hard'}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {explicitSave ? (
          <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="small"
              className="w-full sm:w-auto"
              disabled={isSaving || disabled}
              onClick={syncDraftFromProps}
            >
              Reset
            </Button>
            <Button
              type="button"
              variant="primary"
              size="small"
              className="w-full sm:w-auto"
              disabled={isSaving || disabled || !isDirty}
              onClick={() => void persist()}
            >
              {isSaving ? 'Saving…' : 'Save preference'}
            </Button>
          </div>
        ) : (
          <div className="flex min-h-[1rem] items-center">
            {isSaving ? <span className="text-xs text-text-muted">Saving…</span> : null}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'inline') {
    const selectTypeValue =
      draftType === 'prefer_mixed' || draftType === 'prefer_levels' ? draftType : 'prefer_mixed';
    return (
      <div className="space-y-3" aria-busy={isSaving}>
        {preferenceEnabled ? (
          <div className="space-y-3">
            <FormField label="Type" className="mb-0">
              <Select
                value={selectTypeValue}
                onChange={onGameTypeChange}
                options={INLINE_GAME_TYPE_OPTIONS}
                placeholder=""
                disabled={isSaving || disabled}
                className="w-full min-h-[2.75rem] text-sm"
              />
            </FormField>

            <div className="form-group mb-0">
              <div className="mb-1.5 flex items-center gap-1.5">
                <span className="form-label mb-0" id="match-pref-strictness-label">
                  Strictness
                </span>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-text-muted transition hover:bg-surface-subtle hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  title={STRICTNESS_INFO}
                  aria-label="Explain soft and hard strictness"
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                  </svg>
                </button>
              </div>
              <p id="match-pref-strictness-desc" className="sr-only">
                {STRICTNESS_INFO}
              </p>
              <Select
                value={draftStrength === 'hard' ? 'hard' : 'soft'}
                onChange={onStrengthChange}
                options={INLINE_STRENGTH_OPTIONS}
                placeholder=""
                disabled={isSaving || disabled}
                className="w-full min-h-[2.75rem] text-sm"
                aria-labelledby="match-pref-strictness-label"
                aria-describedby="match-pref-strictness-desc"
              />
            </div>
          </div>
        ) : (
          <p className="text-xs leading-snug text-text-muted">
            Turn on the switch above to choose Mixed or Levels and how strictly it applies.
          </p>
        )}

        <div className="flex min-h-[1rem] items-center">
          {isSaving ? <span className="text-xs text-text-muted">Saving…</span> : null}
        </div>
      </div>
    );
  }

  const formBody = (
    <div className="flex flex-col gap-4 p-4 pt-2 sm:p-4">
      <fieldset className="space-y-2">
        <legend className="sr-only">Match type preference</legend>
        <div className="grid grid-cols-1 gap-2 sm:gap-2.5">
          {TYPE_OPTIONS.map((opt) => {
            const selected = draftType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={isSaving}
                onClick={() => {
                  setDraftType(opt.value);
                  if (opt.value === 'no_preference') setDraftStrength('soft');
                }}
                className={`flex w-full min-h-[3.25rem] items-start gap-3 rounded-xl border px-3 py-3 text-left transition sm:min-h-0 sm:py-2.5 ${
                  selected
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/35'
                    : 'border-border-medium bg-surface-subtle hover:border-border hover:bg-surface'
                } ${isSaving ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <span className="text-xl leading-none sm:text-lg" aria-hidden>
                  {opt.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1">
                    <span className="block text-sm font-semibold text-text-primary">{opt.label}</span>
                    {opt.infoText ? (
                      <PreferenceInfoButton
                        text={opt.infoText}
                        ariaLabel="Explain Any lineup"
                        className="h-6 w-6"
                      />
                    ) : null}
                  </span>
                </span>
                {selected ? (
                  <span className="shrink-0 text-primary" aria-hidden>
                    ✓
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </fieldset>

      {showStrength ? (
        <div className="rounded-xl border border-border-medium bg-surface-subtle p-3">
          <div className="mb-2 text-xs font-medium text-text-secondary">How strict?</div>
          <p className="mb-3 text-xs leading-snug text-text-muted">
            <strong className="text-text-secondary">Soft</strong> — you may still get other lineups (scoring adjusts).
            <br />
            <strong className="text-text-secondary">Hard</strong> — you only play when that type is possible from the
            queue (can mean a longer wait).
          </p>
          <div
            className="flex rounded-lg border border-border-medium bg-surface p-0.5"
            role="group"
            aria-label="Preference strength"
          >
            {(['soft', 'hard']).map((s) => (
              <button
                key={s}
                type="button"
                disabled={isSaving}
                onClick={() => setDraftStrength(s)}
                className={`flex-1 rounded-md py-2.5 text-sm font-medium transition sm:py-2 ${
                  draftStrength === s
                    ? 'bg-primary text-text-inverse shadow-sm'
                    : 'text-text-secondary hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                }`}
              >
                {s === 'soft' ? 'Soft' : 'Hard'}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          size="small"
          className="w-full sm:w-auto"
          disabled={isSaving}
          onClick={() => {
            syncDraftFromProps();
            closeUi();
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="small"
          className="w-full sm:w-auto"
          disabled={isSaving || !isDirty}
          onClick={persist}
        >
          {isSaving ? 'Saving…' : 'Save preference'}
        </Button>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <Modal isOpen={modalOpen} onClose={onClose} closeOnBackdropClick={!isSaving}>
        <ModalContent
          size="medium"
          padding={false}
          className="flex min-h-0 flex-col overflow-hidden"
          aria-labelledby="match-pref-modal-title"
        >
          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border-medium px-4 py-3">
            <div className="min-w-0">
              <div id="match-pref-modal-title" className="text-base font-semibold text-text-primary">
                Match preference
              </div>
              {playerName ? <div className="mt-0.5 truncate text-xs text-text-muted">{playerName}</div> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md p-1.5 text-text-muted transition hover:bg-surface-subtle hover:text-text-primary"
              aria-label="Close"
            >
              <span aria-hidden className="text-xl leading-none">
                ×
              </span>
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{formBody}</div>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={`flex h-7 w-7 items-center justify-center rounded-full text-sm transition sm:h-6 sm:w-6 sm:text-xs ${
          isHard
            ? 'bg-warning/15 text-warning-dark ring-1 ring-warning/40 hover:bg-warning/25'
            : hasActivePreference
              ? 'bg-primary/10 text-primary hover:bg-primary/20'
              : 'bg-surface-subtle text-text-muted hover:bg-surface'
        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        title={getButtonTitle()}
        aria-label={`Set match preference for ${playerName || 'player'}`}
      >
        <span className="leading-none">{getButtonIcon()}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} aria-hidden />
          <div className="absolute left-0 top-full z-50 mt-1 w-[min(100vw-1.5rem,20rem)] max-w-[min(100vw-1.5rem,20rem)] rounded-xl border border-border-medium bg-surface shadow-lg">
            <div className="border-b border-border-medium px-3 py-2">
              <div className="text-xs font-semibold text-text-primary">Match preference</div>
            </div>
            <div className="max-h-[min(70vh,24rem)] overflow-y-auto">{formBody}</div>
          </div>
        </>
      )}
    </div>
  );
});

MatchPreferenceSelector.displayName = 'MatchPreferenceSelector';

export default MatchPreferenceSelector;
